﻿import React, { useState, useEffect } from 'react';
import { IcGradient } from '../models/ic-gradient';
import { CrudFunctions } from '../utilities/crud-functions';
import { Entities } from '../models/enums';
import IcGradientsComponent from './IcGradientsComponent';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import AuditHistoryComponent from './AuditHistoryComponent';

import DataGrid, {
    Column,
    Paging,
    Button as ColumnButton,
    Toolbar,
    Item as ToolbarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

let key = 1;

export interface Props {
    benchSheetId?: string;
    isReadOnly: boolean;
    datagridHeight?: string;
    title?: string;
}

export default function IcGradientsComponentData(props: Props) {
    const [gradients, setGradients] =
        useState<IcGradient[]>();

    const [selectedGradient, setSelectedGradient] =
        useState<IcGradient>();

    const [gradientsEditorDialogVisible, setGradientsEditorDialogVisible] =
        useState<boolean>(false);

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getGradients();
    }, [props.benchSheetId]);

    key++;

    return (
        <div className="pw-content">
            <div id="analysis-data-datagrid-container" className="pw-datagrid">
                <DataGrid
                    height={props.datagridHeight ? props.datagridHeight : "100%"}
                    keyExpr="gradientId"
                    dataSource={gradients ? gradients : []}
                    rowAlternationEnabled={true}
                    onSelectionChanged={gradientSelectionChanged}
                    showRowLines={true}
                    showBorders={true} >
                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />

                    <Column type="buttons" width='5rem' >
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={showAuditInformationDialog} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onGradientInformationButtonClicked} />
                    </Column>

                    <Column dataField='rowNumber' dataType="number"
                        alignment='center' width='10rem' />
                    <Column dataField='time' dataType="number" width={'15%'} />
                    <Column dataField='concentration' dataType="number" format={decimalFormat} width={'15%'} />
                    <Column />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">{props.title ? props.title : "IC Gradients"}</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem location="after">
                            <Button hint="Edit" icon="inserttable"
                                disabled={props.isReadOnly}
                                width="1.8rem" onClick={showGradientsEditorDialog} />
                        </ToolbarItem>

                    </Toolbar>
                </DataGrid>

            </div>

            {gradientsEditorDialogVisible &&
                <DialogPopup
                    visible={gradientsEditorDialogVisible}
                    onHiding={hideGradientsEditorDialog}
                    dragEnabled={false}
                    width="50rem" height="32rem"
                    maxWidth="80vw" maxHeight="80vh"
                    fullScreen={false}
                    hideOnOutsideClick={false}
                    title="IC Gradients">
                    <IcGradientsComponent benchSheetId={props.benchSheetId}
                        onClose={onCloseGradientsEditor} />
                </DialogPopup>
            }

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={Entities.ICGradients}
                        key={`AuditHistory${key}`}
                        childData={false}
                        rowPointer={selectedGradient && selectedGradient.gradientId ?
                            selectedGradient.gradientId : undefined} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onGradientInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            setSelectedGradient(e.row.data);
            showGradientInformationDialog(e.row.data);
        }
    }

    function gradientSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedGradient(e.selectedRowsData[0]);
        }
    }

    async function onCloseGradientsEditor() {
        //refresh data:
        await getGradients();
        hideGradientsEditorDialog();
    }

    // CRUD functions

    async function getGradients() {
        await CrudFunctions.getData(`benchsheets/icgradientsbybenchsheetid/${props.benchSheetId}`)
            .then((data) => {
                setGradients(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Gradients");
            });
    }

    // show/hide

    function showGradientInformationDialog(gradient: IcGradient) {
        if (gradient) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: gradient.gradientId,
                createTimestamp: gradient.createTimestamp,
                lastModifiedTimestamp: gradient.modifyTimestamp,
                createUser: gradient.createUser,
                modifyUser: gradient.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showGradientsEditorDialog() {
        setGradientsEditorDialogVisible(true);
    }

    function hideGradientsEditorDialog() {
        setGradientsEditorDialogVisible(false);
    }

    function showAuditInformationDialog() {
        setAuditInformationDialogVisible(true);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}


